import React, { useCallback } from 'react'
import { l } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { FlowCardClickAction } from 'apps/cms/pages/ContentManagementSystem/lib/types'
import { contentManagementActions, contentManagementSelectors } from 'apps/cms/store'
import { openIconBlockedModal } from 'common/core/actions/uiActions'
import { getPageId } from 'common/core/selectors/appSelectors'
import { showAgenticFlowUnavailableToast } from 'common/flow/actions/agenticFlowActions'
import { ICON_PRODUCT_NAME } from 'common/fusion/constants'
import { FusionAnalytics } from 'common/fusion/lib/fusionAnalytics'
import { getCanOpenAgenticFlow, getIsIconBlocked } from 'common/fusion/selectors'
import { FsFlow } from 'shared/api/requests/cms/schemas'
import { getIconAutomationUrl } from 'utils/url'

import { useFlowCardValidation } from './useFlowCardValidation'

export const useExternalFlowCard = ({ flow }: { flow: FsFlow }) => {
  const dispatch = useAppDispatch()
  const flowId = flow.ns
  const { flowCardClickAction } = useAppSelector(contentManagementSelectors.getUi)
  const canOpenAgenticFlow = useAppSelector(getCanOpenAgenticFlow)
  const isIconBlocked = useAppSelector(getIsIconBlocked)
  const pageId = useAppSelector(getPageId)
  const { isCardDisabled, errorMessage } = useFlowCardValidation(flow)

  let cardActionAriaLabel = l.translate('Open {automationName}', { automationName: flow.name })
  if (flowCardClickAction === FlowCardClickAction.SAVE_FLOW_ID_TO_STATE) {
    cardActionAriaLabel = l.translate('Select {automationName}', { automationName: flow.name })
  } else if (canOpenAgenticFlow) {
    cardActionAriaLabel = l.translate('Open {automationName} in {productName}', {
      automationName: flow.name,
      productName: ICON_PRODUCT_NAME,
    })
  }

  const handleFlowCardClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement | HTMLAnchorElement>) => {
      event.preventDefault()

      switch (flowCardClickAction) {
        case FlowCardClickAction.SAVE_FLOW_ID_TO_STATE:
          dispatch(contentManagementActions.setLastSelectedFlowId(flowId))
          break
        case FlowCardClickAction.OPEN_FLOW:
          if (!canOpenAgenticFlow) {
            dispatch(showAgenticFlowUnavailableToast())
            break
          }
          if (isIconBlocked) {
            dispatch(openIconBlockedModal('automation'))
            break
          }
          FusionAnalytics.logAutomationCardClick()
          window.open(getIconAutomationUrl({ pageId, flowId }), '_blank', 'noopener,noreferrer')
          break
      }
    },
    [dispatch, flowCardClickAction, flowId, canOpenAgenticFlow, isIconBlocked, pageId],
  )

  return {
    handleFlowCardClick,
    cardActionAriaLabel,
    isCardDisabled,
    errorMessage,
  }
}
